import { useState, useEffect } from 'react'
import { Save, Copy, RefreshCw, Plus, Trash2, Eye, EyeOff, Palette, Code, CreditCard, PhoneForwarded, Send } from 'lucide-react'
import BillingCard from '../components/BillingCard'
import { useTranslation } from '@/i18n'
import { API_BASE_URL } from '@/config'
import { auth } from '@/firebase'
import { useAuth } from '@/hooks/useAuth'

interface TeamMember {
  id: string
  email: string
  role: 'admin' | 'member' | 'agent'
  status: 'online' | 'away' | 'offline'
  createdAt: string
}

export default function Settings() {
  const { t } = useTranslation()
  const { user } = useAuth()
  const [activeTab, setActiveTab] = useState('widget')
  const [saving, setSaving] = useState(false)
  const [showApiKey, setShowApiKey] = useState(false)
  const [loadingProject, setLoadingProject] = useState(true)
  const [projectId, setProjectId] = useState<string | null>(null)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const [widgetSettings, setWidgetSettings] = useState({
    theme: 'light',
    primaryColor: '#4f46e5',
    position: 'bottom-right',
    welcomeMessage: 'Hi! How can I help you today?',
    placeholder: 'Type your message...',
    showAvatar: true,
    showTimestamp: true,
    allowFileUpload: false
  })

  const [apiSettings, setApiSettings] = useState({
    primaryKey: '',
    webhookUrl: '',
    rateLimit: 100
  })

  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([])
  const [loadingTeam, setLoadingTeam] = useState(false)
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member')
  const [inviting, setInviting] = useState(false)

  const [escalationSettings, setEscalationSettings] = useState({
    enabled: false,
    telegramBotToken: '',
    telegramChatId: '',
    triggerKeywords: '',
    handoffMessage: 'Your conversation is being transferred to a human agent. Please wait.'
  })
  const [testingEscalation, setTestingEscalation] = useState(false)
  const [testResult, setTestResult] = useState<string | null>(null)

  // Fetch project data on mount
  useEffect(() => {
    async function fetchProject() {
      setLoadingProject(true)
      try {
        const token = await auth.currentUser?.getIdToken()
        if (!token) return

        // Get project list, pick the first one
        const listRes = await fetch(`${API_BASE_URL}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!listRes.ok) return

        const listBody = await listRes.json()
        const projects = listBody.projects || []
        if (projects.length === 0) {
          setLoadingProject(false)
          return
        }

        const pid = projects[0].id
        setProjectId(pid)

        // Now get full project details
        const detailRes = await fetch(`${API_BASE_URL}/projects/${pid}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        if (!detailRes.ok) {
          setLoadingProject(false)
          return
        }

        const project = await detailRes.json()

        // Populate widget settings from project config
        const config = project.config || {}
        setWidgetSettings(prev => ({
          ...prev,
          ...(config.widget || {}),
          welcomeMessage: config.widget?.welcomeMessage || config.personality || prev.welcomeMessage,
          theme: config.widget?.theme || prev.theme,
          primaryColor: config.widget?.primaryColor || prev.primaryColor,
          position: config.widget?.position || prev.position,
          placeholder: config.widget?.placeholder || prev.placeholder,
          showAvatar: config.widget?.showAvatar ?? prev.showAvatar,
          showTimestamp: config.widget?.showTimestamp ?? prev.showTimestamp,
          allowFileUpload: config.widget?.allowFileUpload ?? prev.allowFileUpload
        }))

        // Populate API key from project data
        const apiKeys = project.apiKeys || []
        if (apiKeys.length > 0) {
          setApiSettings(prev => ({
            ...prev,
            primaryKey: apiKeys[0].keyPrefix || apiKeys[0].key || prev.primaryKey
          }))
        }

        // Populate escalation settings from project config
        const esc = config.escalation || {}
        setEscalationSettings(prev => ({
          ...prev,
          enabled: esc.enabled ?? prev.enabled,
          telegramBotToken: esc.telegramBotToken || prev.telegramBotToken,
          telegramChatId: esc.telegramChatId || prev.telegramChatId,
          triggerKeywords: Array.isArray(esc.triggerKeywords) ? esc.triggerKeywords.join(', ') : (esc.triggerKeywords || prev.triggerKeywords),
          handoffMessage: esc.handoffMessage || prev.handoffMessage
        }))
      } catch (err) {
        console.error('Failed to fetch project settings:', err)
      } finally {
        setLoadingProject(false)
      }
    }
    fetchProject()
  }, [])

  // Fetch team members when team tab is selected
  const fetchTeamMembers = async () => {
    setLoadingTeam(true)
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return
      const res = await fetch(`${API_BASE_URL}/team/members`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      if (res.ok) {
        const body = await res.json()
        setTeamMembers((body.members || []).map((m: any) => ({
          id: m.id,
          email: m.email,
          name: m.name,
          role: m.role || 'agent',
          status: m.status || 'offline',
          createdAt: m.createdAt ? new Date(m.createdAt).toISOString().split('T')[0] : ''
        })))
      } else {
        setTeamMembers([])
      }
    } catch {
      setTeamMembers([])
    } finally {
      setLoadingTeam(false)
    }
  }

  useEffect(() => {
    if (activeTab !== 'team') return
    fetchTeamMembers()
  }, [activeTab])

  const handleSave = async () => {
    if (!projectId) return
    setSaving(true)
    setSaveError(null)
    setSaveSuccess(false)
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        setSaveError('Not authenticated')
        return
      }

      // Build config update based on active tab
      const configUpdate: any = {}

      if (activeTab === 'widget') {
        configUpdate.widget = { ...widgetSettings }
      } else if (activeTab === 'escalation') {
        configUpdate.escalation = {
          enabled: escalationSettings.enabled,
          telegramBotToken: escalationSettings.telegramBotToken,
          telegramChatId: escalationSettings.telegramChatId,
          triggerKeywords: escalationSettings.triggerKeywords.split(',').map(k => k.trim()).filter(Boolean),
          handoffMessage: escalationSettings.handoffMessage
        }
      } else if (activeTab === 'api') {
        configUpdate.webhookUrl = apiSettings.webhookUrl
        configUpdate.rateLimit = apiSettings.rateLimit
      }

      const res = await fetch(`${API_BASE_URL}/projects/${projectId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ config: configUpdate })
      })

      if (res.ok) {
        setSaveSuccess(true)
        setTimeout(() => setSaveSuccess(false), 3000)
      } else {
        const errBody = await res.json().catch(() => ({}))
        setSaveError(errBody.message || 'Failed to save settings')
      }
    } catch (err) {
      console.error('Failed to save settings:', err)
      setSaveError('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const generateNewApiKey = async () => {
    if (!projectId) return
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return
      const res = await fetch(`${API_BASE_URL}/projects/${projectId}/apikeys`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ name: 'API Key' })
      })
      if (res.ok) {
        const body = await res.json()
        setApiSettings(prev => ({ ...prev, primaryKey: body.key || prev.primaryKey }))
      }
    } catch (err) {
      console.error('Failed to generate API key:', err)
    }
  }

  const copyApiKey = () => {
    navigator.clipboard.writeText(apiSettings.primaryKey)
  }

  const [inviteError, setInviteError] = useState<string | null>(null)

  const handleRemoveMember = async (memberId: string) => {
    if (!confirm('Remove this member?')) return
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return
      await fetch(`${API_BASE_URL}/team/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      })
      setTeamMembers(prev => prev.filter(m => m.id !== memberId))
    } catch { /* ignore */ }
  }

  const handleInviteUser = async () => {
    if (!inviteEmail) return
    setInviting(true)
    setInviteError(null)
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) return

      const res = await fetch(`${API_BASE_URL}/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: inviteEmail,
          role: inviteRole === 'admin' ? 'admin' : 'agent',
          name: inviteEmail.split('@')[0],
        })
      })

      const body = await res.json()

      if (res.ok) {
        setInviteEmail('')
        setShowInviteModal(false)
        setInviteError(null)
        // Refresh team list
        fetchTeamMembers()
      } else {
        setInviteError(body.message || 'Failed to invite user')
      }
    } catch (err) {
      setInviteError('Network error')
    } finally {
      setInviting(false)
    }
  }

  const handleTestEscalation = async () => {
    if (!projectId) return
    setTestingEscalation(true)
    setTestResult(null)
    try {
      const token = await auth.currentUser?.getIdToken()
      if (!token) {
        setTestResult('error')
        return
      }

      // Try sending a test message via Telegram directly
      // Since there's no dedicated test endpoint, we use the Telegram API directly
      if (escalationSettings.telegramBotToken && escalationSettings.telegramChatId) {
        const telegramRes = await fetch(`https://api.telegram.org/bot${escalationSettings.telegramBotToken}/sendMessage`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            chat_id: escalationSettings.telegramChatId,
            text: `[Test] Escalation test from ClawCustomer dashboard.\nProject: ${projectId}\nTime: ${new Date().toISOString()}`
          })
        })
        if (telegramRes.ok) {
          setTestResult('success')
        } else {
          setTestResult('error')
        }
      } else {
        setTestResult('error')
      }
    } catch (error) {
      setTestResult('error')
    } finally {
      setTestingEscalation(false)
    }
  }

  const tabs = [
    { id: 'widget', name: t('settings.widgetConfiguration'), icon: Palette },
    { id: 'api', name: t('settings.apiSettings'), icon: Code },
    { id: 'escalation', name: t('settings.escalation'), icon: PhoneForwarded },
    { id: 'billing', name: t('settings.billingUsage'), icon: CreditCard },
    { id: 'team', name: t('settings.teamManagement'), icon: Plus }
  ]

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">{t('settings.title')}</h1>
        <p className="mt-1 text-sm text-gray-500">{t('settings.subtitle')}</p>
      </div>

      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`group inline-flex items-center py-2 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
            >
              <tab.icon className={`-ml-0.5 mr-2 h-5 w-5 ${activeTab === tab.id ? 'text-indigo-500' : 'text-gray-400 group-hover:text-gray-500'}`} />
              {tab.name}
            </button>
          ))}
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        {activeTab === 'widget' && (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('settings.widgetAppearance')}</h3>
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('settings.theme')}</label>
                  <select value={widgetSettings.theme} onChange={(e) => setWidgetSettings({ ...widgetSettings, theme: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                    <option value="light">{t('settings.light')}</option>
                    <option value="dark">{t('settings.dark')}</option>
                    <option value="auto">{t('settings.auto')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('settings.primaryColor')}</label>
                  <div className="mt-1 flex items-center space-x-3">
                    <input type="color" value={widgetSettings.primaryColor} onChange={(e) => setWidgetSettings({ ...widgetSettings, primaryColor: e.target.value })} className="h-10 w-20 rounded-md border border-gray-300" />
                    <input type="text" value={widgetSettings.primaryColor} onChange={(e) => setWidgetSettings({ ...widgetSettings, primaryColor: e.target.value })} className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('settings.position')}</label>
                  <select value={widgetSettings.position} onChange={(e) => setWidgetSettings({ ...widgetSettings, position: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500">
                    <option value="bottom-right">{t('settings.bottomRight')}</option>
                    <option value="bottom-left">{t('settings.bottomLeft')}</option>
                    <option value="top-right">{t('settings.topRight')}</option>
                    <option value="top-left">{t('settings.topLeft')}</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('settings.welcomeMessage')}</label>
                  <input type="text" value={widgetSettings.welcomeMessage} onChange={(e) => setWidgetSettings({ ...widgetSettings, welcomeMessage: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700">{t('settings.messagePlaceholder')}</label>
                  <input type="text" value={widgetSettings.placeholder} onChange={(e) => setWidgetSettings({ ...widgetSettings, placeholder: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" />
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-base font-medium text-gray-900 mb-3">{t('settings.features')}</h4>
              <div className="space-y-3">
                {[
                  { key: 'showAvatar', label: t('settings.showAvatar') },
                  { key: 'showTimestamp', label: t('settings.showTimestamps') },
                  { key: 'allowFileUpload', label: t('settings.allowFileUploads') }
                ].map((feature) => (
                  <div key={feature.key} className="flex items-center">
                    <input type="checkbox" id={feature.key} checked={widgetSettings[feature.key as keyof typeof widgetSettings] as boolean} onChange={(e) => setWidgetSettings({ ...widgetSettings, [feature.key]: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                    <label htmlFor={feature.key} className="ml-3 text-sm font-medium text-gray-700">{feature.label}</label>
                  </div>
                ))}
              </div>
            </div>

            <div className="border-t pt-6">
              <h4 className="text-base font-medium text-gray-900 mb-3">{t('settings.widgetIntegration')}</h4>
              <p className="text-sm text-gray-500 mb-4">{t('settings.widgetIntegrationDesc')}</p>
              <div className="bg-gray-900 rounded-md p-4 relative">
                <button type="button" onClick={() => { const code = `<script>\n(function(w,d,o){\nw[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments)};\nvar s=d.createElement("script");s.async=1;\ns.src="https://cdn.clawcustomer.com/widget.js";\nd.head.appendChild(s);\n})(window,document,"ClawCustomer");\n\nClawCustomer("init", {\n  projectId: "${projectId || 'your-project-id'}",\n  apiKey: "${apiSettings.primaryKey}",\n  theme: "${widgetSettings.theme}",\n  primaryColor: "${widgetSettings.primaryColor}",\n  position: "${widgetSettings.position}"\n});\n</script>`; navigator.clipboard.writeText(code) }} className="absolute top-2 right-2 inline-flex items-center px-2 py-1 text-xs font-medium rounded text-gray-300 bg-gray-700 hover:bg-gray-600">
                  <Copy className="h-3 w-3 mr-1" />
                  {t('common.copy')}
                </button>
                <pre className="text-sm text-green-400 overflow-x-auto"><code>{`<script>
(function(w,d,o){
w[o]=w[o]||function(){(w[o].q=w[o].q||[]).push(arguments)};
var s=d.createElement("script");s.async=1;
s.src="https://cdn.clawcustomer.com/widget.js";
d.head.appendChild(s);
})(window,document,"ClawCustomer");

ClawCustomer("init", {
  projectId: "${projectId || 'your-project-id'}",
  apiKey: "${apiSettings.primaryKey}",
  theme: "${widgetSettings.theme}",
  primaryColor: "${widgetSettings.primaryColor}",
  position: "${widgetSettings.position}"
});
</script>`}</code></pre>
              </div>
              <p className="mt-3 text-xs text-gray-400">{t('settings.asyncLoad')}</p>
            </div>
          </div>
        )}

        {activeTab === 'api' && (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('settings.apiConfiguration')}</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('settings.primaryApiKey')}</label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input type={showApiKey ? 'text' : 'password'} value={apiSettings.primaryKey} readOnly className="flex-1 rounded-none rounded-l-md border-gray-300 bg-gray-50 focus:border-indigo-500 focus:ring-indigo-500 font-mono text-sm" />
                    <button type="button" onClick={() => setShowApiKey(!showApiKey)} className="relative -ml-px inline-flex items-center px-3 py-2 border border-gray-300 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100">
                      {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                    <button type="button" onClick={copyApiKey} className="relative -ml-px inline-flex items-center px-3 py-2 border border-gray-300 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100">
                      <Copy className="h-4 w-4" />
                    </button>
                    <button type="button" onClick={generateNewApiKey} className="relative -ml-px inline-flex items-center px-3 py-2 border border-gray-300 bg-gray-50 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-r-md">
                      <RefreshCw className="h-4 w-4" />
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-gray-500">{t('settings.apiKeySecret')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('settings.webhookUrl')}</label>
                  <input type="url" value={apiSettings.webhookUrl} onChange={(e) => setApiSettings({ ...apiSettings, webhookUrl: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" placeholder="https://your-site.com/webhook" />
                  <p className="mt-1 text-sm text-gray-500">{t('settings.webhookDesc')}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">{t('settings.rateLimit')}</label>
                  <input type="number" value={apiSettings.rateLimit} onChange={(e) => setApiSettings({ ...apiSettings, rateLimit: parseInt(e.target.value) })} className="mt-1 block w-32 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500" min="1" max="1000" />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'escalation' && (
          <div className="p-6 space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">{t('settings.escalationConfiguration')}</h3>
              <p className="text-sm text-gray-500 mb-6">{t('settings.escalationDesc')}</p>
              <div className="space-y-6">
                <div className="flex items-center">
                  <input type="checkbox" id="escalation-enabled" checked={escalationSettings.enabled} onChange={(e) => setEscalationSettings({ ...escalationSettings, enabled: e.target.checked })} className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500" />
                  <label htmlFor="escalation-enabled" className="ml-3 text-sm font-medium text-gray-700">{t('settings.enableEscalation')}</label>
                </div>
                {escalationSettings.enabled && (
                  <div className="space-y-4 pl-7">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('settings.telegramBotToken')}</label>
                      <input type="password" value={escalationSettings.telegramBotToken} onChange={(e) => setEscalationSettings({ ...escalationSettings, telegramBotToken: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-mono text-sm" placeholder="123456789:ABCdefGHIjklMNOpqrsTUVwxyz" />
                      <p className="mt-1 text-sm text-gray-500">Get this from @BotFather on Telegram.</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('settings.telegramChatId')}</label>
                      <input type="text" value={escalationSettings.telegramChatId} onChange={(e) => setEscalationSettings({ ...escalationSettings, telegramChatId: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 font-mono text-sm" placeholder="-1001234567890" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('settings.triggerKeywords')}</label>
                      <input type="text" value={escalationSettings.triggerKeywords} onChange={(e) => setEscalationSettings({ ...escalationSettings, triggerKeywords: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm" placeholder="human agent, speak to someone, escalate, manager" />
                      <p className="mt-1 text-sm text-gray-500">{t('settings.triggerKeywordsDesc')}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">{t('settings.handoffMessage')}</label>
                      <textarea value={escalationSettings.handoffMessage} onChange={(e) => setEscalationSettings({ ...escalationSettings, handoffMessage: e.target.value })} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 text-sm" rows={3} />
                      <p className="mt-1 text-sm text-gray-500">{t('settings.handoffMessageDesc')}</p>
                    </div>
                    <div className="pt-2">
                      <button type="button" onClick={handleTestEscalation} disabled={testingEscalation || !escalationSettings.telegramBotToken || !escalationSettings.telegramChatId} className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                        {testingEscalation ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                        {testingEscalation ? t('settings.sending') : t('settings.sendTestMessage')}
                      </button>
                      {testResult === 'success' && <span className="ml-3 text-sm text-green-600">{t('settings.testSuccess')}</span>}
                      {testResult === 'error' && <span className="ml-3 text-sm text-red-600">{t('settings.testFailed')}</span>}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="p-6">
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900">{t('settings.billingTitle')}</h3>
              <p className="text-sm text-gray-500">{t('settings.billingDesc')}</p>
            </div>
            <BillingCard tenantId={user?.tenantId || ''} />
          </div>
        )}

        {activeTab === 'team' && (
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900">{t('settings.teamMembers')}</h3>
                <p className="text-sm text-gray-500">{t('settings.teamDesc')}</p>
              </div>
              <button onClick={() => setShowInviteModal(true)} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700">
                <Plus className="h-4 w-4 mr-2" />
                {t('settings.inviteMember')}
              </button>
            </div>
            {loadingTeam ? (
              <div className="animate-pulse space-y-4">
                {[...Array(3)].map((_, i) => (
                  <div key={i} className="h-12 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : teamMembers.length === 0 ? (
              <div className="text-center py-8 text-gray-500 text-sm">
                No team members found. Invite members to get started.
              </div>
            ) : (
            <div className="overflow-hidden shadow-sm border border-gray-200 rounded-md">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('auth.email')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('settings.role')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('settings.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('settings.joined')}</th>
                    <th className="relative px-6 py-3"><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {teamMembers.map((member) => (
                    <tr key={member.id}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{member.email}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          member.role === 'superadmin' ? 'bg-indigo-100 text-indigo-800' :
                          member.role === 'admin' ? 'bg-purple-100 text-purple-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>{member.role === 'superadmin' ? 'Super Admin' : member.role === 'admin' ? t('settings.admin') : t('settings.member')}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${member.status === 'online' ? 'bg-green-100 text-green-800' : member.status === 'away' ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>{member.status}</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{member.createdAt}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        {member.role !== 'superadmin' && (
                          <button onClick={() => handleRemoveMember(member.id)} className="text-red-600 hover:text-red-900" title="Remove"><Trash2 className="h-4 w-4" /></button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>
        )}

        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 rounded-b-lg">
          <div className="flex items-center justify-between">
            <div>
              {saveError && <span className="text-sm text-red-600">{saveError}</span>}
              {saveSuccess && <span className="text-sm text-green-600">Settings saved successfully.</span>}
              {loadingProject && <span className="text-sm text-gray-500">Loading project settings...</span>}
            </div>
            <button onClick={handleSave} disabled={saving || loadingProject || !projectId} className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50">
              {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {saving ? t('settings.saving') : t('settings.saveSettings')}
            </button>
          </div>
        </div>
      </div>

      {showInviteModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowInviteModal(false)} />
            <div className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <div>
                <div className="mt-3 text-center sm:mt-0 sm:text-left">
                  <h3 className="text-lg font-semibold leading-6 text-gray-900 mb-4">{t('settings.inviteTeamMember')}</h3>
                  <div className="space-y-4">
                    <div>
                      <label htmlFor="invite-email" className="block text-sm font-medium text-gray-700">{t('settings.emailAddress')}</label>
                      <input type="email" id="invite-email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="colleague@example.com" />
                    </div>
                    <div>
                      <label htmlFor="invite-role" className="block text-sm font-medium text-gray-700">{t('settings.role')}</label>
                      <select id="invite-role" value={inviteRole} onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')} className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm">
                        <option value="member">{t('settings.member')}</option>
                        <option value="admin">{t('settings.admin')}</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
              {inviteError && (
                <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-md">
                  <p className="text-sm text-red-600">{inviteError}</p>
                </div>
              )}
              <div className="mt-5 sm:mt-6 sm:flex sm:flex-row-reverse">
                <button type="button" onClick={handleInviteUser} disabled={inviting || !inviteEmail} className="inline-flex w-full justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50 sm:ml-3 sm:w-auto">{inviting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : null}{t('settings.sendInvitation')}</button>
                <button type="button" onClick={() => { setShowInviteModal(false); setInviteError(null) }} className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">{t('common.cancel')}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
